import { Grid, Icon, Skeleton, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CompanyInformation } from "components/CompanyInformation/CompanyInformation";
import { Link, useParams } from "react-router-dom";
import { useGetCompany } from "hooks/companies/useGetCompany";
import MDAlert from "components/MDAlert";
import getLocalString from "constants/Localization";
import { getPrimaryContact } from "utils/companies/getPrimaryContact";
import { getRoute } from "utils/routing";
import { getSecondaryContacts } from "utils/companies/getSecondaryContacts";
import { isNotNilOrEmpty } from "ramda-adjunct";
import { CompanyProposals } from "modules/CompanyProposals/CompanyProposals";
import { CompanyOpportunities } from "modules/CompanyOpportunities/CompanyOpportunities";
import CompanyHeader from "partials/Header/CompanyHeader";
import { CompanyJobs } from "modules/companies/CompanyJobs/CompanyJobs";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import { UserPermissions } from "generated/graphql";
import CompanyNotes from "modules/CompanyNotes/CompanyNotes";
import CompanyContactsStacked from "components/CompanyContactsStacked/CompanyContactsStacked";
import ContactActivityList from "modules/activities/ContactActivityList/ContactActivityList";
import { InfoItem } from "components/InfoItem/InfoItem";
import { useLayoutEffect, useRef, useState } from "react";

function CompanyPageLoader() {
  return <Skeleton />;
}

export default function ViewCompanyPage() {
  const { companyId } = useParams();
  const { loading, data: company, error } = useGetCompany({ id: companyId });
  const { hasPermissions } = useCheckPermissions();
  const [sectionHeight, setSectionHeight] = useState<number>(0);
  const contactInfoRef = useRef<HTMLDivElement | null>(null);

  const editIcon = hasPermissions(UserPermissions.MANAGE_COMPANIES) && (
    <MDTypography
      component={Link}
      to={getRoute("companies.update", [["companyId", company?.id]])}
      variant="body2"
      color="secondary"
    >
      <Tooltip title="Edit" placement="top">
        <Icon>edit</Icon>
      </Tooltip>
    </MDTypography>
  );

  useLayoutEffect(() => {
    const contactInfoElement = contactInfoRef.current;
    if (!contactInfoElement) {
      return;
    }

    const syncHeight = () => setSectionHeight(contactInfoElement.offsetHeight);

    syncHeight();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(syncHeight);
      resizeObserver.observe(contactInfoElement);
      return () => resizeObserver.disconnect();
    }

    return undefined;
  }, [company?.id, company?.name]);

  if (loading) {
    return <CompanyPageLoader />;
  }

  if (!company) {
    return (
      <DashboardPage>
        <MDBox p={2}>
          <MDAlert color="error">Company not found.</MDAlert>
        </MDBox>
      </DashboardPage>
    );
  }

  const primaryContact = getPrimaryContact(company);
  const secondaryContacts = getSecondaryContacts(company);
  const hasSecondaryContacts = isNotNilOrEmpty(secondaryContacts);
  const notificationTypesItem = {
    label: "Notification types",
    value: company.notificationTypes,
  };

  return (
    <DashboardPage>
      <MDBox mb={2} />
      <CompanyHeader company={company}>
        {error ? (
          <MDBox p={2}>
            <MDAlert color="error">
              {getLocalString("generic.failure")}. Details. {error.message}
            </MDAlert>
          </MDBox>
        ) : (
          <>
            <MDBox mt={5} mb={3}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <MDBox ref={contactInfoRef}>
                    <CompanyInformation company={company} icon={editIcon} />
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox sx={{ height: sectionHeight || "auto", overflowY: "auto" }}>
                    <CompanyContactsStacked
                      primaryContact={primaryContact}
                      secondaryContacts={hasSecondaryContacts ? secondaryContacts : []}
                      icon={editIcon}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox sx={{ height: sectionHeight || "auto", overflowY: "auto" }}>
                    <ContactActivityList
                      contactId={primaryContact?.id ?? null}
                      companyId={company?.id ?? null}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox sx={{ px: 2 }}>
              <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                Enable/Disable Notification Triggers for this Contact
              </MDTypography>
              <MDBox p={2} py={1}>
                <InfoItem item={notificationTypesItem} company={company} notificationLabel="" />
              </MDBox>
            </MDBox>
            <CompanyNotes companyId={company?.id} notes={company?.notes} />
            <CompanyOpportunities company={company} />
            <CompanyProposals company={company} />
            <CompanyJobs company={company} />
          </>
        )}
      </CompanyHeader>
    </DashboardPage>
  );
}
